import React from 'react';
import { ClientProvider as PClientProvider } from '@peloton/api/ClientContext';
import { deAuthenticate } from '@peloton/auth/redux';
import { redirectToNotFound } from '@peloton/navigation';
import type { PropsOf } from '@peloton/react/PropsOf';
import { useOauthErrorHandler } from '@auth/client/useOauthErrorHandler';
import { onAuthRecoveryEvent } from '@auth/monitoring';
import { useReportError } from '@engage/error-reporting/useReportError';
import { useReduxAction } from '@engage/redux/hooks';
import { useLogout } from '@members/auth/OauthProvider';

export const ClientProvider: React.FC<
  React.PropsWithChildren<Omit<PropsOf<typeof PClientProvider>, 'oauthEnabled'>>
> = props => {
  const logout = useLogout();
  const deAuth = useReduxAction(deAuthenticate);
  const notFoundRedirect = useReduxAction(redirectToNotFound);
  const errorReporter = useReportError();
  const { client } = props;

  useOauthErrorHandler(client, {
    onAuthFailure: () => {
      deAuth();
      logout();
    },
    onNotFound: () => notFoundRedirect(),
    onAuthErrorEvent: onAuthRecoveryEvent,
  });

  const didReportTokenMismatch = React.useRef(false);

  React.useEffect(() => {
    if (!client) {
      return undefined;
    }

    const interceptorId = client.interceptors.response.use(response => {
      if (
        response.headers?.['invalidate-token'] === 'True' &&
        !didReportTokenMismatch.current
      ) {
        didReportTokenMismatch.current = true;
        errorReporter(new Error('Token mismatch detected'));
        logout();
      }
      return response;
    });

    return () => {
      client.interceptors.response.eject(interceptorId);
    };
    // eslint-disable-next-line
  }, [client]);

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <PClientProvider {...props} client={client} />;
};
